import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box,
  MenuItem,
} from "@mui/material";
import useAdd from "../../api/useAdd";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";

const AdvanceForm = ({ open, onClose, token }) => {
  const { t } = useTranslation();

  // Hook for adding advance records
  const { handleAdd, loading, resetForm } = useAdd("shop/advances", token);

  // Fetch employees and currencies
  const { data: employeesData, loading: employeeLoading } = useFetchObjects(
    ["employee"],
    "shop/employees",
    token
  );

  const { data: currenciesData, loading: currencyLoading } = useFetchObjects(
    ["currency"],
    "shop/currencies",
    token
  );

  const employees = employeesData?.results || [];
  const currencies = currenciesData?.results || [];

  // Form state
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState("");
  const [amount, setAmount] = useState("");
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [notes, setNotes] = useState("");

  // Auto-fill salary based on employee selection
  useEffect(() => {
    if (selectedEmployee) {
      const employee = employees?.find(
        (emp) => emp.id === parseInt(selectedEmployee)
      );
      setAmount(employee ? employee.salary?.parsedValue : "");
    }
  }, [selectedEmployee, employees]);

  const handleSubmit = () => {
    const formData = {
      employee: selectedEmployee,
      currency: selectedCurrency,
      amount,
      year,
      month,
      payment_date: paymentDate,
      notes,
    };
    handleAdd(formData);
  };

  // Reset form on successful submission
  useEffect(() => {
    if (resetForm) {
      setSelectedEmployee("");
      setSelectedCurrency("");
      setAmount("");
      setYear("");
      setMonth("");
      setPaymentDate("");
      setNotes("");
      onClose();
    }
  }, [resetForm, onClose]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {open ? t("AdvanceIndex.titleAdd") : t("AdvanceIndex.titleEdit")}
      </DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          <TextField
            select
            margin="dense"
            label={t("AdvanceIndex.employee")}
            fullWidth
            required
            value={selectedEmployee}
            onChange={(e) => setSelectedEmployee(e.target.value)}
          >
            {employeeLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              (employees || []).map((emp) => (
                <MenuItem key={emp.id} value={String(emp.id)}>
                  {emp.name}
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            select
            margin="dense"
            label={t("AdvanceIndex.currency")}
            fullWidth
            required
            value={selectedCurrency}
            onChange={(e) => setSelectedCurrency(e.target.value)}
          >
            {currencyLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              (currencies || []).map((cur) => (
                <MenuItem key={cur.id} value={String(cur.id)}>
                  {cur.title}
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            margin="dense"
            label={t("AdvanceIndex.amount")}
            type="number"
            fullWidth
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />

          <TextField
            select
            margin="dense"
            label={t("AdvanceIndex.year")}
            fullWidth
            required
            value={year}
            onChange={(e) => setYear(e.target.value)}
          >
            {Array.from({length: 31}, (_, i) => 2020 + i).map(yearOption => (
              <MenuItem key={yearOption} value={yearOption}>
                {yearOption}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            margin="dense"
            label={t("AdvanceIndex.month")}
            fullWidth
            required
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          >
            {Array.from({length: 12}, (_, i) => i + 1).map(monthOption => (
              <MenuItem key={monthOption} value={monthOption}>
                {new Date(0, monthOption - 1).toLocaleString('default', { month: 'long' })}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            margin="dense"
            label={t("AdvanceIndex.paymentDate")}
            type="datetime-local"
            fullWidth
            required
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            margin="dense"
            label={t("AdvanceIndex.notes")}
            fullWidth
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("AdvanceIndex.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            loading ||
            !selectedEmployee ||
            !selectedCurrency ||
            !amount ||
            !year ||
            !month ||
            !paymentDate
          }
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("AdvanceIndex.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdvanceForm;
